import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { listAdvisorsForCompanyPublic } from '../api/screen2Api';
import type { SignupScreen2 } from '../hooks/useSignupContext';
import { useUnauthRumAction } from '../hooks/useUnauthRumAction';
import { PartnerCompanyTypeahead } from './PartnerCompanyTypeahead';

/**
 * Screen 2 of AccountCreationModal — FP identification.
 *
 * Flow per spec linhas 86-94:
 *   1. Yes/No: "Were you invited by your financial advisor?"
 *   2. If Yes → Partner company dropdown
 *   3. After company selected → Advisor dropdown (or "Advisor not listed" → free text)
 *
 * Submitting collects the answers and hands them up to the parent modal, which
 * persists them in localStorage and proceeds to Kinde. The eventual PATCH
 * /auth/care-seeker/me/fp-association runs in AuthCallback after Kinde returns.
 */

const UNLISTED_VALUE = '__unlisted__';

type Props = {
  onSubmit: (data: SignupScreen2) => void;
  onBack: () => void;
  submitting: boolean;
};

export const AccountCreationModalScreen2 = ({ onSubmit, onBack, submitting }: Props) => {
  const [fpAnswer, setFpAnswer] = useState<'yes' | 'no' | null>(null);
  const [companyId, setCompanyId] = useState<string>('');
  const [advisorId, setAdvisorId] = useState<string>('');
  const [unmatchedAdvisorName, setUnmatchedAdvisorName] = useState('');
  /** Field-tagged error so we can wire aria-invalid/aria-describedby per input. */
  const [error, setError] = useState<
    | { field: 'fpAnswer' | 'company' | 'advisor' | 'unmatchedAdvisor'; message: string }
    | null
  >(null);

  const advisorsQuery = useQuery({
    queryKey: ['pre-login', 'advisors', 'public', companyId],
    queryFn: () => listAdvisorsForCompanyPublic(companyId),
    enabled: fpAnswer === 'yes' && !!companyId && companyId !== UNLISTED_VALUE,
    staleTime: 5 * 60 * 1000,
  });

  // Reset downstream selections when the user changes their answer.
  useEffect(() => {
    if (fpAnswer !== 'yes') {
      setCompanyId('');
      setAdvisorId('');
      setUnmatchedAdvisorName('');
    }
  }, [fpAnswer]);

  // Reset advisor selection when company changes.
  useEffect(() => {
    setAdvisorId('');
  }, [companyId]);

  const trackAction = useUnauthRumAction();

  // Wraps onSubmit so every successful submit fires unauth.signup_modal_screen2_submit
  // with the eventually-determined SignupScreen2 payload (spec linha 170).
  const submit = (data: SignupScreen2) => {
    trackAction('unauth.signup_modal_screen2_submit', {
      selectedFp: data.partnerCompanyId,
      selectedAdvisor: data.advisorId,
      unmatchedAdvisorName: data.unmatchedAdvisorName,
    });
    onSubmit(data);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (fpAnswer === null) {
      setError({ field: 'fpAnswer', message: 'Please answer the question to continue.' });
      return;
    }

    if (fpAnswer === 'no') {
      submit({ fpAnswer: 'no' });
      return;
    }

    // fpAnswer === 'yes'
    if (!companyId) {
      setError({ field: 'company', message: "Please select your financial advisor's company." });
      return;
    }

    // Company step: "Advisor not listed" branch — capture free text, skip advisor.
    if (companyId === UNLISTED_VALUE) {
      if (!unmatchedAdvisorName.trim()) {
        setError({ field: 'unmatchedAdvisor', message: "Please type your advisor's name." });
        return;
      }
      submit({
        fpAnswer: 'yes',
        unmatchedAdvisorName: unmatchedAdvisorName.trim(),
      });
      return;
    }

    if (!advisorId) {
      setError({
        field: 'advisor',
        message: 'Please select your advisor (or choose "Advisor not listed").',
      });
      return;
    }

    // Advisor step: "Advisor not listed" branch — keep company id, capture free text.
    if (advisorId === UNLISTED_VALUE) {
      if (!unmatchedAdvisorName.trim()) {
        setError({ field: 'unmatchedAdvisor', message: "Please type your advisor's name." });
        return;
      }
      submit({
        fpAnswer: 'yes',
        partnerCompanyId: companyId,
        unmatchedAdvisorName: unmatchedAdvisorName.trim(),
      });
      return;
    }

    submit({
      fpAnswer: 'yes',
      partnerCompanyId: companyId,
      advisorId,
    });
  };

  const showCompanyUnlistedFreeText = companyId === UNLISTED_VALUE;
  const showAdvisorUnlistedFreeText =
    !showCompanyUnlistedFreeText && advisorId === UNLISTED_VALUE;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset
        className="space-y-2"
        aria-invalid={error?.field === 'fpAnswer'}
        aria-describedby={error?.field === 'fpAnswer' ? 'signup-fp-answer-error' : undefined}
      >
        <legend className="text-sm font-medium text-gray-900 mb-2">
          Were you invited by your financial advisor?
        </legend>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="fpAnswer"
              value="yes"
              checked={fpAnswer === 'yes'}
              onChange={() => { setFpAnswer('yes'); setError(null); }}
              disabled={submitting}
              className="accent-[#026857]"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="fpAnswer"
              value="no"
              checked={fpAnswer === 'no'}
              onChange={() => { setFpAnswer('no'); setError(null); }}
              disabled={submitting}
              className="accent-[#026857]"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
        {error?.field === 'fpAnswer' && (
          <p id="signup-fp-answer-error" className="mt-1.5 text-xs text-red-600" role="alert">
            {error.message}
          </p>
        )}
      </fieldset>

      {fpAnswer === 'yes' && (
        <div className="space-y-3 pt-2">
          <PartnerCompanyTypeahead
            disabled={submitting}
            errorMessage={error?.field === 'company' ? error.message : null}
            onSelectCompany={(company) => { setCompanyId(company.id); setError(null); }}
            onSelectUnlisted={() => { setCompanyId(UNLISTED_VALUE); setError(null); }}
            onClearSelection={() => setCompanyId('')}
          />

          {!showCompanyUnlistedFreeText && companyId && (
            <div>
              <Label htmlFor="signup-advisor">Specific advisor *</Label>
              <select
                id="signup-advisor"
                value={advisorId}
                onChange={(e) => { setAdvisorId(e.target.value); setError(null); }}
                disabled={submitting || advisorsQuery.isLoading}
                aria-invalid={error?.field === 'advisor'}
                aria-describedby={error?.field === 'advisor' ? 'signup-advisor-error' : undefined}
                className="w-full h-9 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#026857] disabled:opacity-50"
              >
                <option value="">
                  {advisorsQuery.isLoading ? 'Loading advisors...' : 'Select an advisor'}
                </option>
                {advisorsQuery.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.firstName}{a.lastName ? ` ${a.lastName}` : ''}
                  </option>
                ))}
                <option value={UNLISTED_VALUE}>Advisor not listed</option>
              </select>
              {error?.field === 'advisor' && (
                <p id="signup-advisor-error" className="mt-1.5 text-xs text-red-600" role="alert">
                  {error.message}
                </p>
              )}
            </div>
          )}

          {(showCompanyUnlistedFreeText || showAdvisorUnlistedFreeText) && (
            <div>
              <Label htmlFor="signup-advisor-name">Your advisor's name *</Label>
              <Input
                id="signup-advisor-name"
                value={unmatchedAdvisorName}
                onChange={(e) => { setUnmatchedAdvisorName(e.target.value); setError(null); }}
                disabled={submitting}
                placeholder="e.g. John Smith"
                maxLength={255}
                aria-invalid={error?.field === 'unmatchedAdvisor'}
                aria-describedby={
                  error?.field === 'unmatchedAdvisor' ? 'signup-advisor-name-error' : undefined
                }
              />
              {error?.field === 'unmatchedAdvisor' && (
                <p
                  id="signup-advisor-name-error"
                  className="mt-1.5 text-xs text-red-600"
                  role="alert"
                >
                  {error.message}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={submitting}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-[#026857] hover:bg-[#025246] text-white font-bold"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Redirecting…
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </form>
  );
};
